// pages/admin/RewardsManagement.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Gift, Settings, Users, DollarSign, TrendingUp, TrendingDown,
  Calendar, Search, Filter, RefreshCw, CheckCircle, XCircle,
  Clock, AlertCircle, Plus, Edit, Trash2, Eye,
  ChevronLeft, ChevronRight, Wallet, Zap, Award,
  BarChart3, PieChart, Download, FileText, Copy, X
} from 'lucide-react';
import { AdminRewardsAPI, AdminAPIUtils } from '../services/api';

const RewardsManagement = () => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, configs, history
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Configs
  const [configs, setConfigs] = useState([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configForm, setConfigForm] = useState({
    name: '',
    description: '',
    min_deposit_amount: '',
    min_wager_amount: '',
    min_turnover: '',
    reward_type: 'percentage',
    reward_value: '',
    max_reward: '',
    eligible_roles: '',
    is_active: true
  });
  
  // History
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });
  const [historyFilters, setHistoryFilters] = useState({
    status: 'all',
    month_year: '',
    search: '',
    user_id: ''
  });
  
  // Generate/Distribute
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [distributeMonth, setDistributeMonth] = useState('');
  const [generateData, setGenerateData] = useState({
    config_id: '',
    month_year: ''
  });

  // Modal state for confirmation
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  useEffect(() => {
    fetchStats();
    fetchConfigs();
    fetchHistory();
  }, [activeTab, pagination.page, historyFilters.status, historyFilters.month_year]);

  // Helper function to show confirmation modal instead of confirm()
  const showConfirmation = (title, message, onConfirm, confirmText = 'Confirm', cancelText = 'Cancel') => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        if (onConfirm) onConfirm();
      },
      confirmText,
      cancelText
    });
  };

  const fetchStats = async () => {
    try {
      const response = await AdminRewardsAPI.getStats();
      if (response.success) {
        setStats(response.stats);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchConfigs = async () => {
    try {
      const response = await AdminRewardsAPI.getConfigs();
      if (response.success) {
        setConfigs(response.configs || []);
      }
    } catch (err) {
      console.error('Failed to fetch configs:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      if (historyFilters.status !== 'all') {
        params.status = historyFilters.status;
      }
      if (historyFilters.month_year) {
        params.month_year = historyFilters.month_year;
      }
      if (historyFilters.search) {
        params.search = historyFilters.search;
      }
      if (historyFilters.user_id) {
        params.user_id = historyFilters.user_id;
      }
      
      const response = await AdminRewardsAPI.getHistory(params);
      if (response.success) {
        setHistory(response.rewards || []);
        setPagination({
          page: response.pagination?.page || 1,
          limit: response.pagination?.limit || 20,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.pages || 1
        });
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setProcessing(true);
      const data = {
        ...configForm,
        min_deposit_amount: parseFloat(configForm.min_deposit_amount) || 0,
        min_wager_amount: parseFloat(configForm.min_wager_amount) || 0,
        min_turnover: parseFloat(configForm.min_turnover) || 0,
        reward_value: parseFloat(configForm.reward_value) || 0,
        max_reward: parseFloat(configForm.max_reward) || null,
        is_active: configForm.is_active ? 1 : 0
      };
      
      if (editingConfig) {
        data.id = editingConfig.id;
      }
      
      const response = await AdminRewardsAPI.saveConfig(data);
      if (response.success) {
        // Use alert or toast notification
        window.alert(editingConfig ? '✅ Config updated successfully!' : '✅ Config created successfully!');
        setShowConfigModal(false);
        resetConfigForm();
        fetchConfigs();
        fetchStats();
      } else {
        window.alert('❌ Failed to save config: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Save config error:', err);
      window.alert('❌ Failed to save config');
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerateRewards = async () => {
    try {
      setProcessing(true);
      const response = await AdminRewardsAPI.generateRewards(
        generateData.config_id,
        generateData.month_year
      );
      
      if (response.success) {
        window.alert(`✅ Generated ${response.created} rewards successfully!`);
        setShowGenerateModal(false);
        setGenerateData({ config_id: '', month_year: '' });
        fetchStats();
        fetchHistory();
      } else {
        window.alert('❌ Failed to generate rewards: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Generate rewards error:', err);
      window.alert('❌ Failed to generate rewards');
    } finally {
      setProcessing(false);
    }
  };

  const handleDistributeRewards = async (monthYear) => {
    showConfirmation(
      'Distribute Rewards',
      `Distribute rewards for ${monthYear}? This will credit users' wallets.`,
      async () => {
        try {
          setProcessing(true);
          const response = await AdminRewardsAPI.distributeRewards(monthYear);
          
          if (response.success) {
            window.alert(`✅ Distributed ${response.distributed} rewards totaling ${response.total_amount} BTC`);
            fetchStats();
            fetchHistory();
          } else {
            window.alert('❌ Failed to distribute rewards: ' + (response.error || 'Unknown error'));
          }
        } catch (err) {
          console.error('Distribute rewards error:', err);
          window.alert('❌ Failed to distribute rewards');
        } finally {
          setProcessing(false);
        }
      },
      'Yes, Distribute',
      'Cancel'
    );
  };

  const handleCalculateActivity = async () => {
    const monthYear = window.prompt('Enter month/year (YYYY-MM):', new Date().toISOString().slice(0, 7));
    if (!monthYear) return;
    
    try {
      setProcessing(true);
      const response = await AdminRewardsAPI.calculateActivity(monthYear);
      
      if (response.success) {
        window.alert(`✅ ${response.message}`);
      } else {
        window.alert('❌ Failed to calculate activity: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Calculate activity error:', err);
      window.alert('❌ Failed to calculate activity');
    } finally {
      setProcessing(false);
    }
  };

  const resetConfigForm = () => {
    setConfigForm({
      name: '',
      description: '',
      min_deposit_amount: '',
      min_wager_amount: '',
      min_turnover: '',
      reward_type: 'percentage',
      reward_value: '',
      max_reward: '',
      eligible_roles: '',
      is_active: true
    });
    setEditingConfig(null);
  };

  const openEditConfig = (config) => {
    setEditingConfig(config);
    setConfigForm({
      name: config.name,
      description: config.description || '',
      min_deposit_amount: config.min_deposit_amount || '',
      min_wager_amount: config.min_wager_amount || '',
      min_turnover: config.min_turnover || '',
      reward_type: config.reward_type,
      reward_value: config.reward_value,
      max_reward: config.max_reward || '',
      eligible_roles: config.eligible_roles || '',
      is_active: config.is_active === 1
    });
    setShowConfigModal(true);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: {
        bg: 'bg-yellow-500/10',
        text: 'text-yellow-400',
        border: 'border-yellow-500/20',
        icon: <Clock className="w-3 h-3" />,
        label: 'Pending'
      },
      processing: {
        bg: 'bg-blue-500/10',
        text: 'text-blue-400',
        border: 'border-blue-500/20',
        icon: <RefreshCw className="w-3 h-3 animate-spin" />,
        label: 'Processing'
      },
      completed: {
        bg: 'bg-green-500/10',
        text: 'text-green-400',
        border: 'border-green-500/20',
        icon: <CheckCircle className="w-3 h-3" />,
        label: 'Completed'
      },
      failed: {
        bg: 'bg-rose-500/10',
        text: 'text-rose-400',
        border: 'border-rose-500/20',
        icon: <XCircle className="w-3 h-3" />,
        label: 'Failed'
      }
    };
    
    const configItem = config[status] || config.pending;
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} ${configItem.border} flex items-center gap-1.5`}>
        {configItem.icon}
        {configItem.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBTC = (amount) => {
    if (!amount) return '0.00000000';
    return parseFloat(amount).toFixed(8);
  };

  // Stats Cards
  const statCards = stats ? [
    {
      title: 'Total Rewards Given',
      value: stats.total?.total_count || 0,
      amount: formatBTC(stats.total?.total_amount || 0) + ' BTC',
      icon: <Gift className="w-5 h-5" />,
      color: 'from-purple-500 to-pink-600'
    },
    {
      title: 'Total BTC Distributed',
      value: stats.total?.total_amount ? formatBTC(stats.total.total_amount) + ' BTC' : '0 BTC',
      icon: <Wallet className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-600'
    },
    {
      title: 'Average Reward',
      value: stats.total?.avg_amount ? formatBTC(stats.total.avg_amount) + ' BTC' : '0 BTC',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'from-blue-500 to-cyan-600'
    },
    {
      title: 'Pending Rewards',
      value: stats.pending?.count || 0,
      amount: formatBTC(stats.pending?.amount || 0) + ' BTC',
      icon: <Clock className="w-5 h-5" />,
      color: 'from-yellow-500 to-amber-600'
    }
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
            🎁 Monthly Rewards
          </h1>
          <p className="text-charcoal-gray mt-1">Manage and distribute monthly rewards to users</p>
        </div>
        <div className="flex items-center space-x-3 flex-wrap gap-2">
          <button
            onClick={handleCalculateActivity}
            disabled={processing}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02] disabled:opacity-50"
          >
            <BarChart3 className="w-4 h-4" />
            Calculate Activity
          </button>
          <button
            onClick={() => {
              resetConfigForm();
              setShowConfigModal(true);
            }}
            className="px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02]"
          >
            <Plus className="w-4 h-4" />
            New Config
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-800">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'dashboard'
              ? 'text-purple-400 border-b-2 border-purple-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          Dashboard
        </button>
        <button
          onClick={() => setActiveTab('configs')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'configs'
              ? 'text-purple-400 border-b-2 border-purple-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Settings className="w-4 h-4" />
          Configurations
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'history'
              ? 'text-purple-400 border-b-2 border-purple-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FileText className="w-4 h-4" />
          Reward History
        </button>
      </div>

      {activeTab === 'dashboard' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((card, index) => (
              <div 
                key={index} 
                className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:bg-night-blue/70 transition-all duration-300 hover:scale-[1.02] group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                    {card.icon}
                  </div>
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{card.value}</p>
                  <p className="text-charcoal-gray text-sm uppercase tracking-wider">{card.title}</p>
                </div>
                {card.amount && (
                  <div className="mt-4 pt-4 border-t border-gray-800">
                    <p className="text-sm text-emerald-400 font-medium">{card.amount}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Monthly Chart */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-400" />
              Monthly Rewards Distribution
            </h3>
            <div className="space-y-4">
              {stats?.by_month?.map((month, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-24 text-sm text-charcoal-gray">{month.month_year}</div>
                  <div className="flex-1 bg-gray-800 rounded-full h-4 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-purple-500 to-pink-500 h-full rounded-full transition-all duration-1000"
                      style={{ 
                        width: `${Math.min((month.amount / (stats.total?.total_amount || 1)) * 100, 100)}%` 
                      }}
                    />
                  </div>
                  <div className="w-32 text-sm text-right">
                    <span className="text-white font-medium">{month.count} rewards</span>
                    <span className="text-emerald-400 ml-2">{formatBTC(month.amount)} BTC</span>
                  </div>
                </div>
              ))}
              {(!stats?.by_month || stats.by_month.length === 0) && (
                <p className="text-charcoal-gray text-center py-8">No reward data available yet</p>
              )}
            </div>
          </div>

          {/* Top Users */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-400" />
              Top Rewarded Users
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-800">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Rewards Received</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Total Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {stats?.top_users?.map((user, index) => (
                    <tr key={index} className="hover:bg-gray-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xs">
                            {user.username?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <span className="text-white font-medium">{user.username}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-white">{user.reward_count}</td>
                      <td className="px-4 py-3 text-emerald-400 font-medium">{formatBTC(user.total_reward)} BTC</td>
                    </tr>
                  ))}
                  {(!stats?.top_users || stats.top_users.length === 0) && (
                    <tr>
                      <td colSpan="3" className="px-4 py-8 text-center text-charcoal-gray">
                        No users have received rewards yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'configs' && (
        <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-white">Reward Configurations</h3>
            <button
              onClick={() => {
                resetConfigForm();
                setShowConfigModal(true);
              }}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300"
            >
              <Plus className="w-4 h-4" />
              Add Config
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-800">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Min Deposit</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Min Wager</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Reward</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {configs.map((config) => (
                  <tr key={config.id} className="hover:bg-gray-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-white font-medium">{config.name}</div>
                        <div className="text-xs text-charcoal-gray">{config.description}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white">{formatBTC(config.min_deposit_amount)} BTC</td>
                    <td className="px-4 py-3 text-white">{formatBTC(config.min_wager_amount)} BTC</td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="text-emerald-400 font-medium">
                          {config.reward_type === 'percentage' ? `${config.reward_value}%` : `${formatBTC(config.reward_value)} BTC`}
                        </span>
                        {config.max_reward && (
                          <div className="text-xs text-charcoal-gray">Max: {formatBTC(config.max_reward)} BTC</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${config.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                        {config.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditConfig(config)}
                          className="p-2 bg-gray-800 hover:bg-gray-700 text-blue-400 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setGenerateData({
                              config_id: config.id,
                              month_year: new Date().toISOString().slice(0, 7)
                            });
                            setShowGenerateModal(true);
                          }}
                          className="p-2 bg-gray-800 hover:bg-gray-700 text-emerald-400 rounded-lg transition-colors"
                          title="Generate Rewards"
                        >
                          <Gift className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {configs.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-charcoal-gray">
                      No reward configurations found. Create your first one!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h3 className="text-lg font-bold text-white">Reward History</h3>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => {
                  setHistoryFilters({
                    status: 'all',
                    month_year: '',
                    search: '',
                    user_id: ''
                  });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search user..."
                  value={historyFilters.search}
                  onChange={(e) => setHistoryFilters(prev => ({ ...prev, search: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && fetchHistory()}
                  className="w-full pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>
            </div>
            
            <div>
              <select
                value={historyFilters.status}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            
            <div>
              <input
                type="month"
                value={historyFilters.month_year}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, month_year: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                placeholder="Month"
              />
            </div>
            
            <div>
              <input
                type="text"
                placeholder="User ID"
                value={historyFilters.user_id}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, user_id: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-purple-500 transition-colors"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-800">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Month</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Activity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Reward</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-charcoal-gray uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {history.map((reward) => (
                  <tr key={reward.id} className="hover:bg-gray-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xs">
                          {reward.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <div className="text-white text-sm font-medium">{reward.username}</div>
                          <div className="text-xs text-charcoal-gray">{reward.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white">{reward.month_year}</td>
                    <td className="px-4 py-3">
                      <div className="space-y-1 text-sm">
                        <div className="text-charcoal-gray">Deposits: <span className="text-white">{formatBTC(reward.total_deposits)} BTC</span></div>
                        <div className="text-charcoal-gray">Wagers: <span className="text-white">{formatBTC(reward.total_wagers)} BTC</span></div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-emerald-400 font-bold text-lg">{formatBTC(reward.reward_amount)} BTC</div>
                      <div className="text-xs text-charcoal-gray">{reward.config_name || 'No config'}</div>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(reward.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {reward.status === 'pending' && (
                          <button
                            onClick={() => handleDistributeRewards(reward.month_year)}
                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs transition-colors"
                          >
                            Distribute
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-charcoal-gray">
                      No reward history found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-800">
              <div className="text-sm text-charcoal-gray">
                Page <span className="text-white">{pagination.page}</span> of{' '}
                <span className="text-white">{pagination.totalPages}</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Config Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-night-blue border border-gray-800 rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">
                {editingConfig ? 'Edit Reward Config' : 'Create Reward Config'}
              </h3>
              <button
                onClick={() => {
                  setShowConfigModal(false);
                  resetConfigForm();
                }}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Config Name *</label>
                <input
                  type="text"
                  value={configForm.name}
                  onChange={(e) => setConfigForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Monthly Rewards - Bronze Tier"
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                <textarea
                  value={configForm.description}
                  onChange={(e) => setConfigForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this reward configuration"
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors resize-none"
                  rows="2"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Min Deposit (BTC)</label>
                  <input
                    type="number"
                    value={configForm.min_deposit_amount}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, min_deposit_amount: e.target.value }))}
                    step="0.00000001"
                    placeholder="0"
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Min Wager (BTC)</label>
                  <input
                    type="number"
                    value={configForm.min_wager_amount}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, min_wager_amount: e.target.value }))}
                    step="0.00000001"
                    placeholder="0"
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Min Turnover (BTC)</label>
                  <input
                    type="number"
                    value={configForm.min_turnover}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, min_turnover: e.target.value }))}
                    step="0.00000001"
                    placeholder="0"
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Reward Type</label>
                  <select
                    value={configForm.reward_type}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, reward_type: e.target.value }))}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                    <option value="bonus">Bonus (Tiered)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {configForm.reward_type === 'percentage' ? 'Reward %' : 'Reward Amount (BTC)'}
                  </label>
                  <input
                    type="number"
                    value={configForm.reward_value}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, reward_value: e.target.value }))}
                    step={configForm.reward_type === 'percentage' ? '0.01' : '0.00000001'}
                    placeholder={configForm.reward_type === 'percentage' ? '5' : '0.001'}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Max Reward (BTC) - Optional</label>
                  <input
                    type="number"
                    value={configForm.max_reward}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, max_reward: e.target.value }))}
                    step="0.00000001"
                    placeholder="Unlimited"
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={configForm.is_active}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4 accent-purple-500"
                  />
                  <span className="text-gray-300 text-sm">Active</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-6 border-t border-gray-800">
              <button
                onClick={() => {
                  setShowConfigModal(false);
                  resetConfigForm();
                }}
                className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfig}
                disabled={processing || !configForm.name || !configForm.reward_value}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {editingConfig ? 'Update Config' : 'Create Config'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Rewards Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-night-blue border border-gray-800 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Gift className="w-5 h-5 text-purple-400" />
                Generate Rewards
              </h3>
              <button
                onClick={() => {
                  setShowGenerateModal(false);
                  setGenerateData({ config_id: '', month_year: '' });
                }}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Month/Year *</label>
                <input
                  type="month"
                  value={generateData.month_year}
                  onChange={(e) => setGenerateData(prev => ({ ...prev, month_year: e.target.value }))}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                />
                <p className="text-xs text-charcoal-gray mt-1">Will generate rewards for this month</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-6 border-t border-gray-800">
              <button
                onClick={() => {
                  setShowGenerateModal(false);
                  setGenerateData({ config_id: '', month_year: '' });
                }}
                className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateRewards}
                disabled={processing || !generateData.month_year || !generateData.config_id}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Gift className="w-4 h-4" />
                    Generate Rewards
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
       {/* Confirmation Modal */}
       {confirmModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-night-blue border border-gray-800 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-white">{confirmModal.title}</h3>
                <p className="text-gray-300 mt-2">{confirmModal.message}</p>
              </div>
              <button
                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>
            <div className="flex gap-3 mt-6 pt-6 border-t border-gray-800">
              <button
                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
              >
                {confirmModal.cancelText}
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white rounded-xl transition-all duration-300"
              >
                {confirmModal.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardsManagement;